import { useEffect, useState } from 'react'
import { Collapse } from '@mui/material'
import { CalendarBlankIcon } from '@phosphor-icons/react'

import { Button } from '@components/shared/Button'
import { Input } from '@components/shared/Input'
import { KeyboardArrowDown } from '@assets/svgs/KeyboardArrowDown'
import { KeyboardArrowUp } from '@assets/svgs/KeyboardArrowUp'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { useUser } from '@hooks/useUser'
import { useConsolidatedEntry } from '@pages/ConsolidatedEntry/context'
import { toast } from '@utils/functions/notificationToast'

import { getAccountIcon } from '../ConsolidatedGrid/helpers/getAccountIcon'
import { getAccountBadgeBackground } from './helpers/getAccountBadgeColors'
import { buildInitialAccountValues } from './helpers/buildInitialAccountValues'
import { buildSubmitPayload } from './helpers/buildSubmitPayload'
import { isAccountInputDisabled } from './helpers/isAccountInputDisabled'
import { getWeekStatusLabel } from '../../helpers/getWeekStatusLabel'
import { BudgetPreviewsStatus } from '@components/shared/BudgetPreviewsStatus'

import type { PreviewsStatusProps } from '@components/shared/BudgetPreviewsStatus/types'

import * as S from './styles'

export const InsertValuesCard = () => {
  const { filiData } = useUser()
  const { consolidatedData, isSubmitLoading, submitPreviaSemanal } = useConsolidatedEntry()

  const [isOpen, setIsOpen] = useState(false)
  const [values, setValues] = useState<Record<number, string>>({})

  const accounts = consolidatedData?.accounts ?? []
  const weeks = consolidatedData?.weeks ?? []
  const currentWeek = weeks.find((week) => week.isCurrent)

  useEffect(() => {
    setValues(buildInitialAccountValues(accounts, weeks))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consolidatedData])

  const handleCancel = () => {
    setValues(buildInitialAccountValues(accounts, weeks))
  }

  const handleConfirm = () => {
    if (!filiData?.startPeriod || !filiData?.filiCode) return

    const hasBlankValue = accounts.some((account) => !values[account.accountId])

    if (hasBlankValue) {
      toast.error('Preencha todos os valores antes de confirmar.')

      return
    }

    submitPreviaSemanal(buildSubmitPayload(accounts, values, filiData.startPeriod, filiData.filiCode))
  }

  return (
    <S.Container>
      <S.TopRow onClick={() => setIsOpen(!isOpen)}>
        <S.TitleColumn>
          <S.Title>Inserir Valores</S.Title>
          <S.Subtitle>Aqui você vai inserir os valores das contas referentes à competência vigente.</S.Subtitle>
        </S.TitleColumn>

        <S.ToggleButton>{isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</S.ToggleButton>
      </S.TopRow>

      <S.WarningBox>
        <S.WarningIconWrapper>
          <WarningIcon />
        </S.WarningIconWrapper>
        {isOpen
          ? 'Preencha as projeções totais do mês para todas as contas referente a semana atual. Os cálculos do DRE serão atualizados em tempo real ao salvar.'
          : 'O botão de liberar prévia ficará habilitado assim que a inserção dos valores for concluída.'}
      </S.WarningBox>

      <Collapse in={isOpen} unmountOnExit>
        <S.ExpandedContent>
          <S.WeekHeader>
            <S.WeekHeaderIcon>
              <CalendarBlankIcon size={24} />
            </S.WeekHeaderIcon>

            <S.WeekHeaderTexts>
              <S.WeekHeaderTitle>{currentWeek ? `Semana ${currentWeek.weekNumber}` : ''}</S.WeekHeaderTitle>
              {!!currentWeek?.status && (
                <S.WeekHeaderSubtitle>
                  <BudgetPreviewsStatus
                    compact
                    status={{
                      code: currentWeek.status as PreviewsStatusProps,
                      descriptionPtbr: getWeekStatusLabel(currentWeek.status)
                    }}
                  />
                </S.WeekHeaderSubtitle>
              )}
            </S.WeekHeaderTexts>
          </S.WeekHeader>

          <S.AccountsList>
            {accounts.map((account) => {
              const { icon, color } = getAccountIcon(account.account)
              const canEditCurrentWeek =
                account.weeks.find((week) => week.weekNumber === currentWeek?.weekNumber)?.canEdit ?? true

              return (
                <S.AccountRow key={account.accountId}>
                  <S.AccountInfo>
                    <S.AccountBadge background={getAccountBadgeBackground(account.account)} color={color}>
                      {icon}
                    </S.AccountBadge>
                    <S.AccountLabel>{account.account}</S.AccountLabel>
                  </S.AccountInfo>

                  <S.AccountInputWrapper>
                    <Input
                      name={`account-${account.accountId}`}
                      label="Valor"
                      required
                      hasController={false}
                      maskType="currency"
                      placeholder="Insira o valor"
                      disabled={isAccountInputDisabled(
                        consolidatedData?.permissions?.canSubmit ?? false,
                        isSubmitLoading,
                        canEditCurrentWeek
                      )}
                      value={values[account.accountId] ?? ''}
                      onChange={(e) =>
                        setValues((prev) => ({ ...prev, [account.accountId]: e.target.value }))
                      }
                    />
                  </S.AccountInputWrapper>
                </S.AccountRow>
              )
            })}
          </S.AccountsList>

          <S.FooterActions>
            <Button
              width={140}
              colorType="outlineBlue"
              disabled={!consolidatedData?.permissions?.canSubmit}
              onClick={handleCancel}
            >
              Cancelar
            </Button>
            <Button
              width={180}
              colorType="solidBlue"
              loading={isSubmitLoading}
              disabled={!consolidatedData?.permissions?.canSubmit}
              onClick={handleConfirm}
            >
              Confirmar Valores
            </Button>
          </S.FooterActions>
        </S.ExpandedContent>
      </Collapse>
    </S.Container>
  )
}
